import React, { useCallback, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useInterval, useLocalStorage } from 'usehooks-ts';
import { RcFile } from 'antd/es/upload';

import './index.less';

import {
  AgentMessageModel,
  AgentModel,
  AgentResourceModel,
  AgentRunModel,
  AgentRunStatus,
  AgentThreadModel,
  ChatMessageContentInner,
  DataPayload,
  MessageRole,
  SessionModel,
  SessionProviderModel,
  SessionProviderStatus,
  SessionStatus,
  FilePurpose,
  SessionAssertByAgentRequestSourceTypeEnum,
  SessionCreationByAgentRequestSourceTypeEnum,
  DataPayloadType,
} from '../../../../../gamma-api';
import { alertsSelectorAdd } from '../../../../recoil';
import { defaultApi } from '../../../../apis';
import { LARGE_PAGE_SIZE } from '../../../../constants';
import AgentTestingChat from '../../../../components/AgentTestingChat';
import { buildSessionKey } from '../../../../components/AuthSession/utils';
import { StagedAttachment } from '../../../../components/AgentTestingChat/types';
import { getPayloadsTextContent } from '../../../../utils/messageUtil';

import {
  buildThreadCreationRequest,
  getAttachmentPublicUrl,
  mapAttachmentsToDataPayloads,
  mapResourceToDataPayload,
} from './utils';

const USER_MESSAGE_TEMPORARY_ID = 'user-message-temporary-id';
const AGENT_MESSAGE_TEMPORARY_ID = 'agent-message-temporary-id';
const RUN_CHECK_INTERVAL = 2000;

const AUTH_SESSION_ID_HEADER_NAME = 'auth-session-id';

export interface ITestingChatTabProps {
  agent?: AgentModel;
}

const TestingChatTab: React.FC<ITestingChatTabProps> = ({ agent }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);
  const { t } = useTranslation();

  const [threadLoading, setThreadLoading] = useState(false);
  const [runCreation, setRunCreation] = useState(false);

  const sessionKey = buildSessionKey();
  const [sessionId, setSessionId] = useLocalStorage<string>(sessionKey, '');
  const [session, setSession] = useState<SessionModel | null>();
  const [sessionLoading, setSessionLoading] = useState(false);
  const [authProviders, setAuthProviders] = useState<SessionProviderModel[]>([]);

  const [thread, setThread] = useState<AgentThreadModel>();
  const [messageList, setMessageList] = useState<AgentMessageModel[]>([]);
  const [run, setRun] = useState<AgentRunModel>();

  const [resourceList, setResourceList] = useState<AgentResourceModel[]>([]);

  const [stagedAttachments, setStagedAttachments] = useState<StagedAttachment[]>([]);

  const isRunProcessing =
    runCreation || run?.status == AgentRunStatus.Pending || run?.status == AgentRunStatus.Processing;
  const isProcessing = isRunProcessing || threadLoading;

  const authOptions = session ? { headers: { [AUTH_SESSION_ID_HEADER_NAME]: session.session_id } } : undefined;

  const tryFetchSession = async () => {
    if (!agent) return;

    setSessionLoading(true);
    let sessionRequired = false;
    try {
      const sessionRequiredResponse = await defaultApi.getSessionRequirement(agent.agent_id);
      sessionRequired = sessionRequiredResponse.data.required;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }

    if (!sessionRequired) {
      setSession(null);
      setSessionLoading(false);
      return;
    }

    let currentSession = null;
    if (sessionId) {
      try {
        const sessionUpdateResponse = await defaultApi.assertSession(sessionId, {
          source_type: SessionAssertByAgentRequestSourceTypeEnum.Agent,
          agent_id: agent.agent_id,
        });
        currentSession = sessionUpdateResponse.data.model;
      } catch (err) {
        setSessionId('');
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }

    if (!currentSession) {
      try {
        const sessionCreationResponse = await defaultApi.createSession({
          source_type: SessionCreationByAgentRequestSourceTypeEnum.Agent,
          agent_id: agent.agent_id,
        });
        currentSession = sessionCreationResponse.data.model;
        setSessionId(sessionCreationResponse.data.model.session_id);
      } catch (err) {
        addAlert({ content: t('Agent: Testing chat: Failed to retrieve auth session'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }

    setSession(currentSession);
    setSessionLoading(false);
  };

  const checkAuthState = async (session: SessionModel) => {
    try {
      const sessionResponse = await defaultApi.getSession(session.session_id);
      const providers = sessionResponse.data.providers.filter((p) =>
        authProviders.some((ap) => ap.authority === p.authority)
      );
      if (providers.some((p) => p.status === SessionProviderStatus.SignedIn)) {
        setSession(sessionResponse.data);
        setAuthProviders(
          authProviders.filter((ap) =>
            providers.some((p) => p.authority === ap.authority && p.status !== SessionProviderStatus.SignedIn)
          )
        );
      }
      if (sessionResponse.data.status === SessionStatus.SignedIn) {
        addAlert({ content: t('Agent: Testing chat: Session sign in success'), type: 'success' });
      }
    } catch (err) {
      addAlert({ content: t('Agent: Testing chat: Failed to retrieve auth session'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const tryFetchThread = async () => {
    if (!agent) return;
    if (session && session.status !== SessionStatus.SignedIn) return;

    try {
      setThreadLoading(true);
      const threadResponse = await defaultApi.tryGetLastAgentThread([agent.agent_id], null, authOptions);
      const thread = threadResponse.data;
      if (thread) {
        setThread(thread);
        const messagesResponse = await defaultApi.searchAgentUserMessages(
          null,
          [thread.thread_id],
          null,
          null,
          0,
          LARGE_PAGE_SIZE,
          true,
          authOptions
        );
        setMessageList(messagesResponse.data.items || []);
      }
    } catch (err) {
      addAlert({ content: t('Agent: Testing chat: Failed to load thread'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setThreadLoading(false);
    }
  };

  const createNewThread = async () => {
    if (!agent) return;
    if (session && session.status !== SessionStatus.SignedIn) return;

    try {
      setThreadLoading(true);
      const response = await defaultApi.createAgentThread(buildThreadCreationRequest(agent.agent_id), authOptions);
      setThread(response.data.model);
      return response.data.model;
    } catch (err) {
      addAlert({ content: t('Agent: Testing chat: Failed to create new thread'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setThreadLoading(false);
    }
  };

  const createAgentRun = async (input: DataPayload[], thread?: AgentThreadModel) => {
    if (!agent) return;
    if (session && session.status !== SessionStatus.SignedIn) return;

    try {
      setRunCreation(true);

      const runThread = thread
        ? thread
        : (await defaultApi.createAgentThread(buildThreadCreationRequest(agent.agent_id), authOptions)).data.model;

      const runResponse = await defaultApi.createAgentRun(
        {
          thread_id: runThread.thread_id,
          input,
        },
        authOptions
      );

      setThread(runThread);
      setRun(runResponse.data.model);
    } catch (err) {
      addAlert({ content: t('Agent: Testing chat: Failed agent run creation request'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setStagedAttachments([]);
      setRunCreation(false);
    }
  };

  const refreshThread = async (threadId: string) => {
    const threadResponse = await defaultApi.getOneAgentUserThread(threadId, authOptions);
    setThread(threadResponse.data);
  };

  const checkRunState = async (
    thread: AgentThreadModel,
    run: AgentRunModel,
    currentMessageList: AgentMessageModel[]
  ) => {
    try {
      const response = await defaultApi.getOneAgentUserRun(run.run_id, authOptions);
      const loadedRun = response.data;

      const messagesResponse = await defaultApi.searchAgentUserMessages(
        null,
        [thread.thread_id],
        null,
        null,
        0,
        LARGE_PAGE_SIZE,
        true,
        authOptions
      );
      const newMessageList = messagesResponse.data.items || [];
      if (newMessageList.length >= currentMessageList.length) {
        setMessageList(newMessageList);
      }

      if (loadedRun.status == AgentRunStatus.Success) {
        await refreshThread(thread.thread_id);
        setRun(undefined);
        return;
      }

      if (loadedRun.status == AgentRunStatus.Error || loadedRun.status == AgentRunStatus.Failure) {
        addAlert({ content: t('Agent: Testing chat: Failed to process agent request'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(loadedRun.problem);
        await refreshThread(thread.thread_id);
        setRun(undefined);
      }
    } catch (err) {
      addAlert({ content: t('Agent: Testing chat: Failed to load agent state'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      setRun(undefined);
    }
  };

  const buildUserMessage = (
    chatMessageContent: ChatMessageContentInner[],
    payloads: DataPayload[],
    resources: AgentResourceModel[]
  ): AgentMessageModel => ({
    message_id: USER_MESSAGE_TEMPORARY_ID,
    created_on: moment().toISOString(),
    folder_id: '',
    agent_id: '',
    thread_id: '',
    run_id: '',
    chat_message: { role: MessageRole.User, content: chatMessageContent },
    payloads,
    events: [],
    resources,
    tools: [],
  });

  const buildAgentMessage = (): AgentMessageModel => ({
    message_id: AGENT_MESSAGE_TEMPORARY_ID,
    created_on: moment().toISOString(),
    folder_id: '',
    agent_id: '',
    thread_id: '',
    run_id: '',
    chat_message: { role: MessageRole.Assistant, content: [{ block_type: 'text', text: '' }] },
    payloads: [],
    events: [],
    resources: [],
    tools: [],
  });

  const onMessageSend = (content: ChatMessageContentInner, payload: DataPayload) => {
    if (isProcessing) {
      return;
    }

    const chatMessageFiles: ChatMessageContentInner[] = stagedAttachments
      .filter((attachment) => attachment.model)
      .map((attachment) => {
        return {
          block_type: 'file',
          mime_type: attachment.model?.content?.mime || attachment.file.type,
          file_name: attachment.model?.name || attachment.file.name,
          url: getAttachmentPublicUrl(attachment),
        };
      });

    const agentMessage = buildAgentMessage();
    const userMessage = buildUserMessage(
      [content, ...chatMessageFiles],
      [payload, ...mapAttachmentsToDataPayloads(stagedAttachments)],
      resourceList
    );

    setMessageList([...messageList, userMessage, agentMessage]);

    const dataPayload = [
      payload,
      ...resourceList.map(mapResourceToDataPayload),
      ...mapAttachmentsToDataPayloads(stagedAttachments),
    ];
    void createAgentRun(dataPayload, thread);
    setResourceList([]);
    setStagedAttachments([]);
  };

  const onRunRetry = async (run: AgentRunModel) => {
    if (isProcessing) {
      return;
    }

    const input = run.input;
    let currentThread = thread;

    if (messageList.length) {
      setMessageList([]);
      setResourceList([]);
      currentThread = await createNewThread();
    }

    const files: ChatMessageContentInner[] = input
      .filter((input) => input.type === DataPayloadType.File)
      .map(({ data: { mime_type, file_name, url } }) => ({
        block_type: 'file',
        mime_type,
        file_name,
        url,
      }));

    const resources: AgentResourceModel[] = input
      .filter((input) => input.type === DataPayloadType.Resource)
      .map((resource) => resource.data);

    const content: ChatMessageContentInner = {
      block_type: 'text',
      text: getPayloadsTextContent(input),
    };

    const agentMessage = buildAgentMessage();
    const userMessage = buildUserMessage([content, ...files], input, resources);

    setMessageList([userMessage, agentMessage]);
    await createAgentRun(input, currentThread);
  };

  const onMessagesClean = () => {
    if (isProcessing) {
      return;
    }

    setMessageList([]);
    setResourceList([]);
    createNewThread().finally();
  };

  const onResourceAdd = (resource: AgentResourceModel) => {
    setResourceList([...resourceList, resource]);
  };

  const onResourceDelete = (resource: AgentResourceModel) => {
    setResourceList(resourceList.filter((res) => res !== resource));
  };

  const onSignIn = (provider: SessionProviderModel) => {
    // eslint-disable-next-line security/detect-non-literal-fs-filename
    provider.redirect && window.open(provider.redirect, '_blank');
    setAuthProviders([...authProviders, provider]);
  };

  const onSignInCancel = (provider: SessionProviderModel) =>
    setAuthProviders(authProviders.filter((ap) => ap.authority !== provider.authority));

  const onSignOut = async () => {
    if (!agent || !session) return;

    try {
      await defaultApi.signOutSession(session.session_id);
      const sessionCreationResponse = await defaultApi.createSession({
        source_type: SessionCreationByAgentRequestSourceTypeEnum.Agent,
        agent_id: agent.agent_id,
      });
      const sessionId = sessionCreationResponse.data.model.session_id;
      setSessionId(sessionId);
      setSession(sessionCreationResponse.data.model);
      setMessageList([]);
      addAlert({ content: t('Agent: Testing chat: Session sign out success'), type: 'success' });
    } catch (err) {
      addAlert({ content: t('Agent: Testing chat: Failed to sign out'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSessionLoading(false);
    }
  };

  const onSessionPropChange = () => {
    if (session !== undefined) {
      void tryFetchThread();
    }
  };
  useEffect(onSessionPropChange, [session]);

  const onAgentPropChange = () => {
    void tryFetchSession();
  };
  useEffect(onAgentPropChange, [agent]);

  useInterval(() => {
    if (run && thread) {
      checkRunState(thread, run, messageList).finally();
    }
  }, RUN_CHECK_INTERVAL);

  useInterval(() => {
    if (session && authProviders.length) {
      checkAuthState(session).finally();
    }
  }, RUN_CHECK_INTERVAL);

  const updateAttachmentState = (uid: string, updates: Partial<StagedAttachment>) => {
    setStagedAttachments((prev) =>
      prev.map((attachment) => (attachment.file.uid === uid ? { ...attachment, ...updates } : attachment))
    );
  };

  const uploadFile = async (attachment: StagedAttachment) => {
    updateAttachmentState(attachment.file.uid, { status: 'uploading' });

    try {
      const response = await defaultApi.createFileFormGeneralFilesFormPost(
        attachment.file.uid,
        [FilePurpose.Agent],
        attachment.file
      );

      updateAttachmentState(attachment.file.uid, {
        status: 'success',
        model: response.data.model,
      });
    } catch (error) {
      addAlert({ content: t('Agent: Testing chat: Failed to upload file'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error('File upload failed:', error);
      updateAttachmentState(attachment.file.uid, {
        status: 'error',
      });
    }
  };

  const onAttachmentsUpload = useCallback((files: RcFile[]) => {
    const newAttachments: StagedAttachment[] = [];

    for (const file of files) {
      const newAttachment: StagedAttachment = {
        file,
        status: 'uploading',
      };

      newAttachments.push(newAttachment);

      uploadFile(newAttachment);
    }

    setStagedAttachments((prev) => [...prev, ...newAttachments]);
  }, []);

  const onAttachmentReUpload = useCallback((attachment: StagedAttachment) => {
    uploadFile(attachment);
  }, []);

  const onAttachmentDelete = useCallback((attachment: StagedAttachment) => {
    setStagedAttachments((prev) => prev.filter((prevAttachment) => prevAttachment.file.uid !== attachment.file.uid));
  }, []);

  return (
    <AgentTestingChat
      agent={agent}
      attachments={stagedAttachments}
      authSession={session}
      codeFolder={`agents/${agent?.external_id || '-'}/input`}
      contextWindow={{ usedTokens: thread?.stats.context_tokens, limit: thread?.context_window_limit }}
      dataInterface={agent?.settings.interface}
      loading={threadLoading || sessionLoading}
      messageList={messageList}
      messageProcessing={isRunProcessing}
      resourceList={resourceList}
      onAttachmentDelete={onAttachmentDelete}
      onAttachmentReUpload={onAttachmentReUpload}
      onAttachmentsUpload={onAttachmentsUpload}
      onClean={onMessagesClean}
      onMessageSend={onMessageSend}
      onResourceAdd={onResourceAdd}
      onResourceDelete={onResourceDelete}
      onRunRetry={onRunRetry}
      onSignIn={onSignIn}
      onSignInCancel={onSignInCancel}
      onSignOut={onSignOut}
    />
  );
};

export default TestingChatTab;
